import find from 'lodash/find'
import includes from 'lodash/includes'
import maxBy from 'lodash/maxBy'
import uniq from 'lodash/uniq'
import {fromJust} from '@freckle/maybe'
import type {TranslateT} from '@freckle/educator-entities/ts/common/helpers/translate'
import {getActiveAndVisibleCourses} from '@freckle/educator-entities/ts/roster/helpers/tagged-courses'
import type {CourseAttrs} from '@freckle/educator-entities/ts/roster/models/course'
import type {CourseMembershipAttrs} from '@freckle/educator-entities/ts/roster/models/course-membership'
import {getStudentsforCourse} from '@freckle/educator-entities/ts/users/logic/students'
import type {StudentAttrs} from '@freckle/educator-entities/ts/users/models/student'
import {
  maxCoursesForFreeEdition,
  maxCoursesForPremium
} from '@freckle/classroom/ts/common/helpers/helpers'
import type {TeacherAttrs} from '@freckle/classroom/ts/users/models/teacher'

export function getBiggestCourse(
  courses: Array<CourseAttrs>,
  students: Array<StudentAttrs>,
  courseMemberships: Array<CourseMembershipAttrs>
): CourseAttrs {
  const activeAndVisibleCourses = getActiveAndVisibleCourses(courses).courses
  if (activeAndVisibleCourses.length === 0) {
    throw 'No default course found!'
  } else {
    return fromJust(
      maxBy(
        activeAndVisibleCourses,
        course => getStudentsforCourse(students, course.id, courseMemberships).length
      ),
      'No default course found!'
    )
  }
}

export function getActiveAndVisibleCoursesExcludingDemo(
  teacher: TeacherAttrs,
  courses: Array<CourseAttrs>,
  students: Array<StudentAttrs>,
  courseMemberships: Array<CourseMembershipAttrs>
): Array<CourseAttrs> {
  const teacherStudentIds = students.filter(s => s.isTeacher).map(s => s.id)
  const courseIdsWithoutTeacherStudents = uniq(
    courseMemberships.filter(m => !includes(teacherStudentIds, m.studentId)).map(m => m.courseId)
  )

  const activeAndVisibleCourses = getActiveAndVisibleCourses(courses).courses

  return activeAndVisibleCourses.filter(
    course =>
      course.teacherId === teacher.id && includes(courseIdsWithoutTeacherStudents, course.id)
  )
}

export function getAnyActiveAndVisibleCourseWithStudent(
  student: StudentAttrs,
  courses: Array<CourseAttrs>,
  courseMemberships: Array<CourseMembershipAttrs>
): CourseAttrs | null | undefined {
  const studentId = student.id
  const activeAndVisibleCourses = getActiveAndVisibleCourses(courses).courses
  const activeAndVisibleCourseIds = activeAndVisibleCourses.map(course => course.id)

  // Get any course membership for the student
  const courseMembershipForStudent = find(
    courseMemberships,
    membership =>
      membership.studentId === studentId && activeAndVisibleCourseIds.includes(membership.courseId)
  )

  return courseMembershipForStudent !== undefined
    ? find(activeAndVisibleCourses, course => course.id === courseMembershipForStudent.courseId)
    : null
}

export function prettifyCourseNameError(rawError: string, t: TranslateT): string {
  return (
    {
      'unique-violation': t('COURSES_UNIQUE_NAME_ERROR'),
      unauthorized: t('COURSES_UNAUTHORIZED_ERROR'),
      'empty-course-name': t('COURSES_EMPTY_COURSE_NAME_ERROR'),
      'max-courses': t('COURSES_MAX_COURSES_ERROR', {maxCourses: maxCoursesForPremium}),
      'no-premium': t('COURSES_MAX_COURSES_FREE_EDITION_ERROR', {
        maxCourses: maxCoursesForFreeEdition
      })
    }[rawError] ?? t('COMMON_ERROR_MESSAGE')
  )
}
